import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, Delete, Equal } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * CalculatorDialog
 * ----------------
 * Lightweight on-screen calculator with pt-BR comma decimal support and
 * area shortcut (A × B). Result in BRL and plain number.
 */
export default function CalculatorDialog({ open, onOpenChange, onUseAsValue }) {
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState(null);
  // Area shortcut
  const [a, setA] = useState("");
  const [b, setB] = useState("");

  useEffect(() => {
    if (!open) { setExpr(""); setResult(null); setA(""); setB(""); }
  }, [open]);

  const push = (ch) => setExpr((s) => (s + ch));
  const clearAll = () => { setExpr(""); setResult(null); };
  const backspace = () => setExpr((s) => s.slice(0, -1));

  const evaluate = () => {
    // Sanitize: allow digits, operators + - * / . , ( ) and spaces
    const sanitized = (expr || "")
      .replace(/,/g, ".")
      .replace(/[^0-9+\-*/.() ]/g, "");
    if (!sanitized.trim()) { setResult(null); return; }
    try {
      // eslint-disable-next-line no-new-func
      const v = Function(`"use strict"; return (${sanitized});`)();
      if (!Number.isFinite(v)) { toast.error("Expressão inválida"); return; }
      setResult(v);
    } catch {
      toast.error("Expressão inválida");
    }
  };

  const area = () => {
    const na = parseFloat((a || "").replace(",", "."));
    const nb = parseFloat((b || "").replace(",", "."));
    if (!(na > 0) || !(nb > 0)) { toast.error("Preencha largura e comprimento"); return; }
    const total = na * nb;
    setExpr(`${na} * ${nb}`);
    setResult(total);
  };

  const applyResult = () => {
    if (!Number.isFinite(result)) { toast.error("Calcule algo primeiro"); return; }
    onUseAsValue?.(Number(result));
    onOpenChange(false);
  };

  const formatBR = (v) =>
    Number.isFinite(v)
      ? v.toLocaleString("pt-BR", { maximumFractionDigits: 4 })
      : "—";

  const buttons = [
    ["7", "8", "9", "/"],
    ["4", "5", "6", "*"],
    ["1", "2", "3", "-"],
    ["0", ",", "(", "+"],
    [")", ".", "", ""],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1e3a8a]">
            <Calculator className="w-5 h-5 text-[#7c3aed]" /> Calculadora rápida
          </DialogTitle>
          <DialogDescription>Faça contas sem sair do app.</DialogDescription>
        </DialogHeader>

        {/* Area shortcut */}
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
          <p className="text-[11px] font-bold text-violet-800 uppercase tracking-wide">Área rápida (m²)</p>
          <div className="flex items-center gap-2">
            <Input value={a} onChange={(e) => setA(e.target.value)} placeholder="Largura" inputMode="decimal" className="h-9 text-sm" data-testid="calc-area-a" />
            <span className="text-slate-500 font-bold">×</span>
            <Input value={b} onChange={(e) => setB(e.target.value)} placeholder="Comprimento" inputMode="decimal" className="h-9 text-sm" data-testid="calc-area-b" />
            <Button size="sm" onClick={area} className="bg-violet-600 hover:bg-violet-700 text-white h-9" data-testid="calc-area-go">
              <Equal className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Expression + result */}
        <div className="bg-slate-900 text-white rounded-lg p-3 font-mono">
          <div className="text-right text-xs text-slate-400 h-5 truncate" data-testid="calc-expr">{expr || " "}</div>
          <div className="text-right text-2xl font-extrabold" data-testid="calc-result">
            {result == null ? "0" : formatBR(result)}
          </div>
        </div>

        {/* Pad */}
        <div className="grid grid-cols-4 gap-1.5">
          {buttons.flat().map((ch, i) => {
            if (!ch) return <div key={i} />;
            const isOp = "+-*/".includes(ch);
            return (
              <button
                key={i}
                type="button"
                onClick={() => push(ch === "," ? "." : ch)}
                data-testid={`calc-key-${ch}`}
                className={`h-11 rounded-lg font-bold text-base active:scale-95 transition-transform ${
                  isOp
                    ? "bg-amber-500 hover:bg-amber-600 text-white"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-900"
                }`}
              >
                {ch}
              </button>
            );
          })}
          <button
            type="button"
            onClick={backspace}
            className="h-11 rounded-lg font-bold bg-rose-100 hover:bg-rose-200 text-rose-700 flex items-center justify-center"
            data-testid="calc-key-back"
          >
            <Delete className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="h-11 rounded-lg font-bold bg-slate-200 hover:bg-slate-300 text-slate-700"
            data-testid="calc-key-clear"
          >
            C
          </button>
          <button
            type="button"
            onClick={evaluate}
            className="h-11 col-span-2 rounded-lg font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="calc-key-eq"
          >
            =
          </button>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          {onUseAsValue && (
            <Button
              onClick={applyResult}
              disabled={!Number.isFinite(result)}
              className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
              data-testid="calc-apply"
            >
              Usar como valor
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
